import { t as uiText } from '../i18n';
import { getAuth } from 'firebase/auth';
import { getCloudRunEndpoint } from '../config/cloudRunConfig';
import type { AnswerRefinedEventData, RAGStreamCallbacks, RAGStreamRequest } from './ragStream';
import { AUX_REFINED_FAILURE_MESSAGE } from './ragStream';

type AdminRagStreamError = Error & {
  code?: string;
};

/**
 * 管理画面向けRAGストリーミング
 * - `workspaceId` ベース
 * - `publishId` なし
 * - サーバ側は `POST /admin-rag-stream`
 */
export async function executeAdminRAGStream(
  request: RAGStreamRequest,
  callbacks: RAGStreamCallbacks
): Promise<void> {
  // 認証トークン取得
  const auth = getAuth();
  const user = auth ? auth.currentUser : null;
  if (!user) {
    throw new Error(uiText("Sign-in required"));
  }
  const idToken = await user.getIdToken();

  const baseUrl = `${getCloudRunEndpoint('ragStream')}/admin-rag-stream`;
  const abortController = new AbortController();

  try {
    const isSameOrigin = baseUrl.startsWith('/');
    const response = await fetch(baseUrl, {
      method: 'POST',
      ...(isSameOrigin ? {} : { mode: 'cors' as RequestMode, credentials: 'include' as RequestCredentials }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
      },
      body: JSON.stringify(request),
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    if (!response.body) {
      throw new Error(uiText("Response body is missing"));
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let currentEventType = 'message';
    let lineBuffer = '';

    const dispatchEvent = (eventType: string, parsed: any) => {
      switch (eventType) {
        case 'connected':
          callbacks.onConnected?.(parsed);
          break;
        case 'status':
          callbacks.onStatus?.(parsed);
          break;
        case 'sources':
          callbacks.onSources?.(parsed);
          break;
        case 'chunk':
          callbacks.onChunk?.(parsed);
          break;
        case 'answer':
          callbacks.onAnswer?.(parsed);
          break;
        case 'answer_refined': {
          // 内部 RAG (rag-stream-service) の参照 URL 補助データ 2-pass 結果。
          // ここを実装し忘れると、サーバーが `status:'ready'` を送信していても
          // クライアントは `default: break` で捨ててしまい、その後 `complete` で
          // `refinedStatus='pending'` のまま残る → onComplete のフォールバックで
          // 「参照リンクからの補足情報を取得できませんでした…」と誤表示される。
          const rawStatus = (parsed as any)?.status;
          const urls: string[] = Array.isArray((parsed as any)?.refinedFromUrls)
            ? (parsed as any).refinedFromUrls
            : [];
          const normalizedStatus: 'ready' | 'failed' =
            rawStatus === 'ready' || rawStatus === 'failed'
              ? rawStatus
              : (typeof (parsed as any)?.content === 'string' && (parsed as any).content.length > 0 && urls.length > 0
                  ? 'ready'
                  : 'failed');
          const payload: AnswerRefinedEventData = {
            content: typeof (parsed as any)?.content === 'string' ? (parsed as any).content : '',
            isComplete: (parsed as any)?.isComplete !== false,
            status: normalizedStatus,
            failureMessage:
              typeof (parsed as any)?.failureMessage === 'string' && (parsed as any).failureMessage.length > 0
                ? (parsed as any).failureMessage
                : (normalizedStatus === 'failed' ? AUX_REFINED_FAILURE_MESSAGE : undefined),
            refinedFromUrls: urls,
            refinedFromSources: Array.isArray((parsed as any)?.refinedFromSources)
              ? (parsed as any).refinedFromSources
              : undefined,
            auxDataItems:
              typeof (parsed as any)?.auxDataItems === 'number' ? (parsed as any).auxDataItems : urls.length,
          };
          callbacks.onAnswerRefined?.(payload);
          break;
        }
        case 'metadata':
          callbacks.onMetadata?.(parsed);
          break;
        case 'complete':
          callbacks.onComplete?.(parsed);
          break;
        case 'session_title':
          callbacks.onSessionTitle?.(parsed);
          break;
        case 'freshness':
          callbacks.onFreshness?.(parsed);
          break;
        case 'error':
          callbacks.onError?.(parsed);
          break;
        default:
          break;
      }
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        lineBuffer += chunk;

        const lines = lineBuffer.split('\n');
        lineBuffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEventType = line.slice(7).trim();
            continue;
          }
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            if (!data) continue;
            try {
              const parsed = JSON.parse(data);
              dispatchEvent(currentEventType, parsed);
            } catch (e) {
              console.warn('❌ [Admin RAG Stream] JSON解析エラー:', e, data);
            }
          }
        }
      }

      // ストリーム終了後に残バッファを処理
      if (lineBuffer.trim()) {
        const remaining = lineBuffer.trim();
        if (remaining.startsWith('event: ')) {
          currentEventType = remaining.slice(7).trim();
        } else if (remaining.startsWith('data: ')) {
          const data = remaining.slice(6).trim();
          if (data) {
            try {
              const parsed = JSON.parse(data);
              dispatchEvent(currentEventType, parsed);
            } catch {
              // 最終残バッファのパース失敗は無視
            }
          }
        }
      }
    } finally {
      reader.releaseLock();
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    callbacks.onError?.({ code: 'STREAM_ERROR', message });
    const handledError = new Error(message) as AdminRagStreamError;
    handledError.code = 'STREAM_ERROR';
    throw handledError;
  } finally {
    abortController.abort();
  }
}

